import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { bloodTestsAPI } from '../../services/api'
import NutritionDialog from '../shared/NutritionDialog'
import { contentLanguage } from '../../i18n/supported'

const DIRECTIONS = ['increase', 'decrease', 'either']

/**
 * VH-221: why a marker cannot be judged — trigger, direction of the
 * distortion, the test that settles it and how to prepare for the next draw.
 * Rule texts come from the backend, translated on demand.
 */
export default function MarkerContextSheet({ bloodTestId, resultId, markerName, open, onClose }) {
  const { t, i18n } = useTranslation()
  const language = contentLanguage(i18n?.language)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['bloodTestContext', bloodTestId, language],
    queryFn: async () => (await bloodTestsAPI.getContext(bloodTestId, language)).data,
    enabled: Boolean(open && bloodTestId),
  })

  const entry = data?.markers?.find((m) => m.result_id === resultId)

  return (
    <NutritionDialog
      open={open}
      onClose={onClose}
      title={t('bloodTests.context.sheetTitle', { marker: entry?.marker_name || markerName || '' })}
    >
      {isLoading && <p className="text-sm text-gray-500">{t('bloodTests.context.loading')}</p>}
      {isError && <p className="text-sm text-danger-600">{t('bloodTests.context.loadError')}</p>}
      {!isLoading && !isError && data && (!entry || entry.findings.length === 0) && (
        <p className="text-sm text-gray-600">{t('bloodTests.context.noFindings')}</p>
      )}
      {entry && entry.findings.length > 0 && (
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            {t(`bloodTests.context.interpretabilityExplained.${entry.interpretability}`, {
              defaultValue: t('bloodTests.context.interpretabilityExplained.limited'),
            })}
          </p>
          {entry.findings.map((finding) => (
            <section key={finding.rule_code} className="rounded-lg border border-gray-200 p-3 space-y-2">
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="font-medium text-gray-900">{t('bloodTests.context.trigger')}</dt>
                  <dd className="text-gray-700">
                    {finding.trigger_detail}
                    {!finding.dose_known && (
                      <span className="ml-1 text-xs text-sky-700">({t('bloodTests.context.doseUnknown')})</span>
                    )}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-900">{t('bloodTests.context.direction')}</dt>
                  <dd className="text-gray-700">
                    {DIRECTIONS.includes(finding.direction)
                      ? t(`bloodTests.context.directions.${finding.direction}`)
                      : finding.direction}
                  </dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-900">{t('bloodTests.context.why')}</dt>
                  <dd className="text-gray-700">{finding.explanation}</dd>
                </div>
                {finding.settling_test && (
                  <div>
                    <dt className="font-medium text-gray-900">{t('bloodTests.context.settlingTest')}</dt>
                    <dd className="text-gray-700">{finding.settling_test}</dd>
                  </div>
                )}
                {finding.preparation && (
                  <div>
                    <dt className="font-medium text-gray-900">{t('bloodTests.context.preparation')}</dt>
                    <dd className="text-gray-700">{finding.preparation}</dd>
                  </div>
                )}
              </dl>
            </section>
          ))}
          <p className="text-xs text-gray-500">{t('bloodTests.context.noAdvice')}</p>
        </div>
      )}
    </NutritionDialog>
  )
}
